<template>
  <div class="mb-5">
    <div class="col-12 d-flex flex-wrap filter-row">
      <div>
        <base-input :label="$t('COMMON.PER_PAGE')">
          <el-select
            class="select-primary pagination-select"
            v-model="pagination.perPage"
            :placeholder="$t('COMMON.PER_PAGE')"
          >
            <el-option
              class="select-primary"
              v-for="item in pagination.perPageOptions"
              :key="item"
              :label="item"
              :value="item"
            >
            </el-option>
          </el-select>
        </base-input>
      </div>
      <div>
        <base-input :label="$t('COMMON.SUPPLIERS')">
          <supplier-selector
            :multiple="false"
            :allowNone="false"
            :showAll="true"
            :placeholder="$t('COMMON.SUPPLIERS')"
            @supplierChanged="(supplier) => (selectedSupplier = supplier)"
          />
        </base-input>
      </div>
      <div>
        <base-input :label="$t('COMMON.CATEGORIES')">
          <category-group-selector
            :allowNone="false"
            :showAll="true"
            :isOnlyModels="false"
            :placeholder="$t('COMMON.CATEGORIES')"
            @categoryChanged="
              (categoryId) => {
                selectedCategory = categoryId;
              }
            "
          />
        </base-input>
      </div>
      <div>
        <base-input
          v-model="query"
          type="search"
          prepend-icon="fas fa-search"
          :placeholder="$t('COMMON.SEARCH_HINT.PRODUCT')"
          :label="$t('COMMON.SEARCH')"
          clearable
        />
      </div>
      <div v-if="isDraft">
        <base-checkbox v-model="displayZeroQuantities">
          <span class="form-control-label">
            {{ $t("COMMON.DISPLAY_ZERO_LINES") }}
          </span>
        </base-checkbox>
      </div>
    </div>

    <div class="col-12 mb-3" v-if="isDraft">
      <base-button
        type="secondary"
        size="sm"
        class="mr-2"
        :disabled="saving"
        @click="copyMondayToWeek"
      >
        <i class="fas fa-copy mr-1"></i>
        {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.COPY_MONDAY_TO_WEEK") }}
      </base-button>
      <base-button
        type="secondary"
        size="sm"
        :disabled="saving"
        @click="duplicatePreviousWeek"
      >
        <i class="fas fa-history mr-1"></i>
        {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.DUPLICATE_PREVIOUS_WEEK") }}
      </base-button>
    </div>

    <div class="col-12" v-if="missingWarehouse">
      <div class="alert alert-warning">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTION_HAS_NO_WAREHOUSE") }}
      </div>
    </div>

    <el-table
      class="table-responsive align-items-center table-flush"
      header-row-class-name="thead-light"
      :data="filteredProducts.slice(startIndex, endIndex)"
    >
      <div slot="empty" v-if="loading">
        <img src="/img/loading.gif" style="height: 100px; width: 100px" />
      </div>

      <el-table-column :label="$t('COMMON.CODE')" prop="code" min-width="200">
        <template v-slot="{ row }">
          <div v-if="row">
            <div style="display: flex; align-items: center; gap: 8px">
              <a href="#" @click.prevent="goToProduct(row)">
                {{ row.name }}
              </a>
            </div>
            <div class="text-muted internal-order-item-excerpt">
              <ul>
                <li>{{ $t("PRODUCTS.CODE") }} : {{ row.code }}</li>
                <li>{{ $t("PRODUCTS.SKU") }} : {{ row.sku }}</li>
              </ul>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.UNIT')"
        class-name="number-column"
        label-class-name="number-column"
        width="90"
      >
        <template v-slot="{ row }">
          {{ row.unitOfMeasure && row.unitOfMeasure.referenceUnit ? row.unitOfMeasure.referenceUnit.name : "" }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.UNIT_PRICE')"
        class-name="number-column"
        label-class-name="number-column"
        width="110"
      >
        <template v-slot="{ row }">
          {{ $formatCurrency(getProductUnitPrice(row), 4) }}
        </template>
      </el-table-column>

      <el-table-column
        v-for="day in weekDays"
        :key="day"
        :label="dayLabel(day)"
        class-name="number-column"
        label-class-name="number-column"
        width="105"
      >
        <template v-slot="{ row }">
          <base-input
            :disabled="!isDraft || saving"
            v-model="quantities[cellKey(row.id, day)]"
            type="number"
            min="0"
            placeholder="0"
            @change="
              (event) => {
                updateCell(row, day, event.target.value);
              }
            "
          />
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.TOTAL_QUANTITY')"
        class-name="number-column"
        label-class-name="number-column"
        width="110"
      >
        <template v-slot="{ row }">
          {{ productTotalQuantity(row.id) }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.TOTAL')"
        class-name="number-column"
        label-class-name="number-column"
        width="120"
      >
        <template v-slot="{ row }">
          {{ $formatCurrency(productTotalAmount(row.id), 4) }}
        </template>
      </el-table-column>
    </el-table>

    <div class="col-12 mt-3">
      <table class="table table-sm">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("ESTABLISHMENT_INTERNAL_ORDERS.DAY_TOTAL") }}</th>
            <th v-for="day in weekDays" :key="day" class="text-right">
              {{ dayLabel(day) }}
            </th>
            <th class="text-right">
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.WEEK_TOTAL") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ $t("COMMON.TOTAL") }}</td>
            <td v-for="day in weekDays" :key="day" class="text-right">
              {{ $formatCurrency(dayTotalAmount(day)) }}
            </td>
            <td class="text-right font-weight-bold">
              {{ $formatCurrency(weekTotalAmount) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap table-footer"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Input } from "element-ui";
import { cloneDeep } from "lodash";
import moment from "moment";
import SupplierSelector from "@/components/SupplierSelector.vue";
import CategoryGroupSelector from "@/components/CategoryGroupSelector.vue";
import defaultEstablishmentInternalOrderItem from "../defaultEstablishmentInternalOrderItem";

export default {
  name: "EstablishmentInternalOrderItemsGrid",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
    SupplierSelector,
    CategoryGroupSelector,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      query: "",
      selectedCategory: null,
      selectedSupplier: null,
      displayZeroQuantities: false,
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [10, 20, 50, 100],
      },
      products: [],
      filteredProducts: [],
      missingWarehouse: false,
      // quantities/items are keyed by `<productId>|<YYYY-MM-DD>`: one order line
      // per product AND per day.
      quantities: {},
      itemsByCell: {},
      productsSignatureLoaded: null,
      lastProductsFetchSignature: null,
      activeProductsRequestToken: null,
    };
  },
  computed: {
    isDraft() {
      return (this.order.status || "DRAFT") === "DRAFT";
    },
    weekDays() {
      if (this.order.week_days && this.order.week_days.length) {
        return this.order.week_days;
      }
      if (!this.order.week_starts_at) {
        return [];
      }
      const start = moment(this.order.week_starts_at);
      const end = this.order.week_ends_at
        ? moment(this.order.week_ends_at)
        : start.clone().add(4, "days");
      const days = [];
      const cursor = start.clone();
      while (cursor.isSameOrBefore(end, "day")) {
        days.push(cursor.format("YYYY-MM-DD"));
        cursor.add(1, "day");
      }
      return days;
    },
    from() {
      return (this.pagination.currentPage - 1) * this.pagination.perPage;
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    total() {
      return this.filteredProducts.length;
    },
    startIndex() {
      return (this.pagination.currentPage - 1) * this.pagination.perPage;
    },
    endIndex() {
      return this.startIndex + this.pagination.perPage;
    },
    weekTotalAmount() {
      return this.weekDays.reduce((carry, day) => carry + this.dayTotalAmount(day), 0);
    },
  },
  methods: {
    cellKey(productId, day) {
      return `${productId}|${day}`;
    },
    dayLabel(day) {
      return moment(day).format("ddd DD/MM");
    },
    goToProduct(product) {
      this.$router.push(this.$objectViewRoute(product));
    },
    /**
     * Internal orders are billed at the SELLING price of the production
     * establishment, so that is the price shown while ordering.
     */
    getProductUnitPrice(product) {
      if (product.warehouseProduct && product.warehouseProduct.selling_price) {
        return product.warehouseProduct.selling_price;
      }
      return product.selling_price || 0;
    },
    productTotalQuantity(productId) {
      return this.weekDays.reduce((carry, day) => {
        const quantity = parseFloat(this.quantities[this.cellKey(productId, day)]);
        return carry + (isNaN(quantity) ? 0 : quantity);
      }, 0);
    },
    productTotalAmount(productId) {
      const product = this.filteredProducts.find((item) => item.id === productId);
      const unitPrice = product ? this.getProductUnitPrice(product) : 0;
      return this.productTotalQuantity(productId) * unitPrice;
    },
    dayTotalAmount(day) {
      return Object.values(this.itemsByCell)
        .filter((item) => item.delivery_date === day)
        .reduce((carry, item) => {
          const total =
            item.total_price ?? item.pricing?.total ?? item.pricing?.subtotal ?? 0;
          return carry + parseFloat(total || 0);
        }, 0);
    },
    async getProducts({ force = false } = {}) {
      const signature = this.createProductsFetchSignature();
      if (!force && signature && this.productsSignatureLoaded === signature) {
        this.filterProducts();
        return;
      }
      if (
        !force &&
        this.loading &&
        signature &&
        this.lastProductsFetchSignature === signature
      ) {
        return;
      }

      const requestToken = Symbol("productsRequest");
      this.activeProductsRequestToken = requestToken;

      try {
        this.loading = true;
        this.lastProductsFetchSignature = signature;

        const warehouseId = this.order?.sourceEstablishment?.warehouse?.id;

        // Without the production warehouse there is nothing legitimate to show:
        // querying unfiltered would list products that establishment does not
        // stock, priced at the catalogue price instead of its own selling price.
        if (!warehouseId) {
          this.products = [];
          // Only a loaded order whose production establishment is known can be
          // said to lack a warehouse: the grid also runs against the default
          // (not yet loaded) order, which must not raise a false alarm.
          this.missingWarehouse = !!(this.order?.id && this.order?.sourceEstablishment?.id);
          this.filterProducts();
          this.loading = false;
          this.lastProductsFetchSignature = null;
          this.activeProductsRequestToken = null;
          return;
        }

        this.missingWarehouse = false;

        const params = {
          sort: "name",
          filter: {
            // Only send filters that carry a value: the API rejects an empty
            // `filter[organization]` with a 400 (`filled` rule).
            ...(this.order.organization?.id
              ? { organization: this.order.organization.id }
              : {}),
            // Only what the production establishment actually stocks...
            warehouse: warehouseId,
            // ...and is allowed to sell, since we bill at the selling price...
            can_be_sold: true,
            // ...restricted to the production cafeteria's producible list
            // when one is configured (empty list = no restriction).
            ...(this.order.sourceCafeteria?.id
              ? { producible_by_cafeteria: this.order.sourceCafeteria.id }
              : {}),
          },
          include:
            "taxGroups,unitOfMeasure,orderUnitOfMeasureUnit,categories,suppliers,unitOfMeasure.referenceUnit",
          page: {
            number: 1,
            size: 999999,
          },
        };

        await this.$store.dispatch("products/list", params);
        const productsArr = await this.$store.getters["products/list"];

        if (this.activeProductsRequestToken !== requestToken) {
          return;
        }

        this.products = productsArr;
        this.filterProducts();
        this.loading = false;
        this.lastProductsFetchSignature = signature;
        this.productsSignatureLoaded = signature;
        this.activeProductsRequestToken = null;
      } catch (error) {
        console.error(error);
        this.$notify({
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
          type: "error",
        });
        if (this.activeProductsRequestToken === requestToken) {
          this.loading = false;
          this.lastProductsFetchSignature = null;
          this.productsSignatureLoaded = null;
          this.activeProductsRequestToken = null;
        }
      }
    },
    createProductsFetchSignature() {
      if (!this.order) {
        return null;
      }
      const orgId = this.order.organization?.id || "";
      const sourceEstId = this.order.sourceEstablishment?.id || "";
      const sourceCafeteriaId = this.order.sourceCafeteria?.id || "";
      const warehouseId = this.order.sourceEstablishment?.warehouse?.id || "";
      return `${orgId}::${sourceEstId}::${sourceCafeteriaId}::${warehouseId}`;
    },
    syncOrderItems() {
      if (!this.order || !this.order.items) {
        return;
      }
      const quantities = {};
      const itemsByCell = {};

      this.order.items.forEach((item) => {
        const storableId = item.storable?.id || item.storable_id;
        const day = item.delivery_date;
        if (!storableId || !day) {
          return;
        }
        const key = this.cellKey(storableId, day);
        quantities[key] = item.quantity;
        itemsByCell[key] = item;
      });

      this.quantities = quantities;
      this.itemsByCell = itemsByCell;
      this.filterProducts();
    },
    updateCell(product, day, quantity) {
      const parsedQty = parseFloat(quantity);
      const key = this.cellKey(product.id, day);

      if (isNaN(parsedQty) || parsedQty < 0) {
        this.resetCell(product.id, day);
        return;
      }

      const foundItem = this.itemsByCell[key];

      if (foundItem && parsedQty === 0) {
        this.$emit("onDeleteOrderItem", foundItem.id);
        return;
      }

      if (!foundItem && parsedQty === 0) {
        return;
      }

      let itemData = cloneDeep(defaultEstablishmentInternalOrderItem);
      if (foundItem) {
        itemData = cloneDeep(foundItem);
      }

      itemData.internalOrder = {
        type: "establishment-internal-orders",
        id: this.order.id,
      };
      itemData.storable = {
        type: "products",
        id: product.id,
      };
      itemData.delivery_date = day;
      itemData.quantity = parsedQty;
      if (product.unitOfMeasure && product.unitOfMeasure.referenceUnit) {
        itemData.unitOfMeasureUnit = product.unitOfMeasure.referenceUnit;
      }

      this.$emit("onSubmitOrderItem", itemData);
    },
    resetCell(productId, day) {
      const key = this.cellKey(productId, day);
      const foundItem = this.itemsByCell[key];
      this.$set(this.quantities, key, foundItem ? foundItem.quantity : 0);
    },
    /**
     * Weekly orders are highly repetitive: copying monday over the week saves the
     * bulk of the keying.
     */
    copyMondayToWeek() {
      const [monday, ...otherDays] = this.weekDays;
      if (!monday || !otherDays.length) return;

      const payloads = [];
      this.filteredProducts.forEach((product) => {
        const quantity = parseFloat(this.quantities[this.cellKey(product.id, monday)]);
        if (isNaN(quantity) || quantity <= 0) return;

        otherDays.forEach((day) => {
          const current = parseFloat(this.quantities[this.cellKey(product.id, day)]);
          if (current === quantity) return;
          payloads.push({ product, day, quantity });
        });
      });

      if (!payloads.length) return;
      this.$emit("onBulkOrderItems", payloads);
    },
    /**
     * Reuse the previous week of the same satellite/production pair.
     */
    async duplicatePreviousWeek() {
      const previousMonday = moment(this.order.week_starts_at)
        .subtract(1, "week")
        .format("YYYY-MM-DD");

      try {
        await this.$store.dispatch("establishmentInternalOrders/list", {
          filter: {
            source_establishment: this.order.sourceEstablishment?.id,
            destination_establishment: this.order.destinationEstablishment?.id,
            week_starts_at: [previousMonday, previousMonday],
          },
          page: { number: 1, size: 1 },
        });
        const previousOrders =
          this.$store.getters["establishmentInternalOrders/list"] || [];

        if (!previousOrders.length) {
          this.$notify({
            type: "warning",
            message: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.NO_PREVIOUS_WEEK"),
          });
          return;
        }

        const previous = previousOrders[0];
        const dayOffset = moment(this.order.week_starts_at).diff(
          moment(previous.week_starts_at),
          "days"
        );

        const payloads = [];
        (previous.items || []).forEach((item) => {
          const productId = item.storable?.id;
          if (!productId || !item.delivery_date) return;

          const day = moment(item.delivery_date).add(dayOffset, "days").format("YYYY-MM-DD");
          if (!this.weekDays.includes(day)) return;

          const product = this.products.find((candidate) => candidate.id === productId);
          if (!product) return;

          payloads.push({ product, day, quantity: parseFloat(item.quantity) });
        });

        if (!payloads.length) {
          this.$notify({
            type: "warning",
            message: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.NO_PREVIOUS_WEEK"),
          });
          return;
        }

        this.$emit("onBulkOrderItems", payloads);
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },
    filterProducts() {
      const query = this.query;
      const augmentedProducts = [...(this.products || [])];
      const productIds = new Set(
        augmentedProducts
          .filter((product) => product && product.id)
          .map((product) => product.id)
      );

      // Keep ordered products visible even when they left the production catalogue.
      Object.values(this.itemsByCell || {}).forEach((item) => {
        const storable = item?.storable;
        if (!storable || !storable.id || productIds.has(storable.id)) {
          return;
        }
        augmentedProducts.push({
          ...storable,
          categories: storable.categories || [],
          suppliers: storable.suppliers || [],
          unitOfMeasure: storable.unitOfMeasure || null,
          warehouseProduct: storable.warehouseProduct || null,
        });
        productIds.add(storable.id);
      });

      this.filteredProducts = augmentedProducts.filter((product) => {
        const matchesQuery =
          !query ||
          this.$unaccent(product.code || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.sku || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.name || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase());

        const matchesQuantityFilter = this.displayZeroQuantities
          ? true
          : this.productTotalQuantity(product.id) > 0;

        const matchCategory =
          !this.selectedCategory ||
          product.categories?.some((category) => category.id === this.selectedCategory);

        const matchSupplier =
          !this.selectedSupplier ||
          product.suppliers?.some((supplier) => supplier.id === this.selectedSupplier);

        return matchesQuery && matchesQuantityFilter && matchCategory && matchSupplier;
      });
    },
  },
  watch: {
    order: {
      handler() {
        this.getProducts();
        this.syncOrderItems();
        this.displayZeroQuantities = (this.order.status || "DRAFT") === "DRAFT";
      },
      deep: true,
      immediate: true,
    },
    query: {
      handler() {
        this.filterProducts();
      },
    },
    displayZeroQuantities: {
      handler() {
        this.filterProducts();
      },
    },
    selectedCategory: {
      handler() {
        this.filterProducts();
      },
    },
    selectedSupplier: {
      handler() {
        this.filterProducts();
      },
    },
  },
};
</script>

<style>
.internal-order-item-excerpt,
.internal-order-item-excerpt * {
  font-size: 0.7rem;
}
.internal-order-item-excerpt ul {
  margin-bottom: 0;
  padding-left: 1rem;
}
</style>
